import React, { useRef, useState } from 'react';
import { assessmentsAPI } from '../../api/client';

const labels = { not_sent: 'Not sent', sent: 'Sent', started: 'Started',
  passed: 'Passed', failed: 'Failed', expired: 'Expired', send_failed: 'Delivery failed' };
const formatDate = value => value ? new Date(value.endsWith('Z') || /[+-]\d\d:\d\d$/.test(value)
  ? value : `${value}Z`).toLocaleString() : '';

export default function RemoteAssessmentPanel({ candidateId, candidateName, assessment, canSend, onChange, onBusy, onFailure }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const pending = useRef(null);
  const sending = useRef(false);
  if (!assessment) return null;
  const status = assessment.status;
  const completed = status === 'passed' || status === 'failed';
  const action = completed || status === 'expired' ? 'retake'
    : status === 'sent' || status === 'started' ? 'resend' : 'send';
  const label = action === 'retake' ? 'Allow Retake' : action === 'resend' ? 'Resend Exam Link' : 'Send Exam Link';
  const disabledReason = assessment.invite_block_reason || (!canSend ? "You don't have permission to send exam invitations." : '');

  const send = async () => {
    if (sending.current || disabledReason) return;
    if (action === 'retake' && !window.confirm(`Allow ${candidateName} a new attempt and email the new exam link? The previous result will be kept.`)) return;
    sending.current = true;
    setBusy(true);
    onBusy?.(true);
    setError('');
    setNotice('');
    try {
      if (!pending.current || pending.current.action !== action) {
        const bytes = window.crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        pending.current = { action, request_id: `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` };
      }
      const { data } = await assessmentsAPI.sendInvite(candidateId, pending.current);
      pending.current = null;
      onChange(data);
      setNotice(`Exam link emailed to ${candidateName}.`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not send the exam link. Please try again.';
      setError(detail);
      onFailure?.(detail);
    } finally {
      sending.current = false;
      setBusy(false);
      onBusy?.(false);
    }
  };

  return (
    <section className="mb-5 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4" aria-label="Candidate exam">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-semibold text-gray-900">English assessment</h4>
          <p className="mt-1 text-sm text-gray-700">{labels[status] || status}
            {completed && ` · ${assessment.pct}% (${assessment.correct}/${assessment.total})`}</p>
        </div>
        <button type="button" onClick={send} disabled={busy || !!disabledReason}
          className="min-h-[44px] rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50">
          {busy ? 'Sending…' : label}
        </button>
      </div>
      {assessment.last_sent_at && <p className="mt-2 text-xs text-gray-600">Last sent: {formatDate(assessment.last_sent_at)}</p>}
      {assessment.expires_at && !completed && <p className="mt-1 text-xs text-gray-600">Submit by: {formatDate(assessment.expires_at)}</p>}
      {completed && <p className="mt-2 text-sm text-gray-600">Pass mark: {assessment.pass_mark_used}% · Submitted: {formatDate(assessment.submitted_at)}</p>}
      {assessment.schedule_block_reason && <p className="mt-2 text-sm text-gray-600">{assessment.schedule_block_reason}</p>}
      {disabledReason && <p className="mt-2 text-sm text-amber-800">{disabledReason}</p>}
      {(error || assessment.send_error) && <p role="alert" className="mt-2 text-sm text-red-700">{error || assessment.send_error}</p>}
      {notice && <p role="status" className="mt-2 text-sm text-green-700">{notice}</p>}
      {completed && <details className="mt-3 rounded-lg bg-white p-3">
        <summary className="cursor-pointer text-sm font-medium text-gray-800">View writing answer</summary>
        <p className="mt-3 whitespace-pre-wrap break-words text-sm text-gray-700">{assessment.writing_text || 'No writing answer submitted.'}</p>
      </details>}
    </section>
  );
}
